'use client';

import { type RumInitConfiguration } from '@datadog/browser-rum';
import { datadogRum } from '@datadog/browser-rum';

import {
  DEPLOYMENT_DOMAIN,
  DeploymentEnvironment,
  NEXT_PUBLIC_DATADOG_APP_ID,
  NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
  NEXT_PUBLIC_DATADOG_SERVICE_NAME,
  NEXT_PUBLIC_DATADOG_SITE,
  NEXT_PUBLIC_RELEASE_VERSION,
} from '@env';
import { getCookieConsentForCategory } from '@modules/cookie-utils';
import { botUserAgentPatterns } from '@modules/monitoring/constants';

type MonitoredEnvironment = 'preview' | 'production';

const botPattern = `(${botUserAgentPatterns.join('|')})`;
const regex = new RegExp(botPattern, 'i');

const sharedRumInitConfig: RumInitConfiguration = {
  applicationId: NEXT_PUBLIC_DATADOG_APP_ID,
  clientToken: NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
  site: NEXT_PUBLIC_DATADOG_SITE,
  service: NEXT_PUBLIC_DATADOG_SERVICE_NAME,
  version: NEXT_PUBLIC_RELEASE_VERSION,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'allow',
  allowedTracingUrls: [
    /^https:\/\/blinq\.me(?!.*\/cdn-cgi)/,
    /https:\/\/.*\.blinq\.me\/graphql/,
    /https:\/\/.*\.blnq\.xyz\/graphql/,
    'https://firestore.googleapis.com',
    'https://googleapis.com',
  ],
};

const rumInitConfigs: { [key in MonitoredEnvironment]: RumInitConfiguration } = {
  production: {
    ...sharedRumInitConfig,
    env: 'production',
    sessionSampleRate: typeof window !== 'undefined' && regex.test(navigator.userAgent) ? 0 : 100,
    sessionReplaySampleRate: typeof window !== 'undefined' && regex.test(navigator.userAgent) ? 0 : 100,
    trackingConsent: getCookieConsentForCategory('performance') ? 'granted' : 'not-granted',
  },
  preview: {
    ...sharedRumInitConfig,
    env: 'non-production',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackingConsent: getCookieConsentForCategory('performance') ? 'granted' : 'not-granted',
  },
};

const isNonProduction = [DeploymentEnvironment.Preview, DeploymentEnvironment.NonProduction].includes(
  DEPLOYMENT_DOMAIN
);
const isProduction = DEPLOYMENT_DOMAIN === DeploymentEnvironment.Production;

if (isProduction || isNonProduction) {
  const rumInitConfig = isNonProduction ? rumInitConfigs['preview'] : rumInitConfigs['production'];
  datadogRum.init(rumInitConfig);
}

export const blinqDatadogRum = datadogRum;
